import XPMotorApi from "@xpeng/xpmotors-jsbridge-api";
import bridge from "@xpeng/js-bridge";
import { Loading, Toast } from "@xp-design/mobile";
import { getBrowserEnv, loadScript } from "./tools";
import { getParamsFromHash } from "./url";
import { wxInit, myInit } from "./mini-program";

// 重新导出，保持向后兼容
export { wxInit, myInit };

/**
 * transformToQueryUrl: 将url转换成查询参数类型的url
 * @param url {string}
 * @param params {object}
 * @returns {string}
 */
export function transformToQueryUrl(url: string, params: any): string {
  let query = "";
  if (params && Object.keys(params).length > 0) {
    query = "?";
    let count = 1;
    for (const ele in params) {
      if (count < Object.keys(params).length) {
        query += ele + "=" + params[ele] + "&";
        count++;
      } else {
        query += ele + "=" + params[ele];
      }
    }
  }
  return url + query;
}

/**
 * 获取当前平台：android还是iOS
 */
export function getPlatform() {
  const u = navigator.userAgent;
  console.log(u, "userAgent");
  const isAndroid =
    u.indexOf("Android") > -1 ||
    u.indexOf("Harmony") > -1 ||
    u.indexOf("Adr") > -1; //判断是否是 android终端
  const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); //判断是否是 iOS终端
  console.log("是否是Android：" + isAndroid); //true,false
  console.log("是否是iOS：" + isIOS);
  if (isAndroid) {
    return "android";
  } else if (isIOS) {
    return "iOS";
  } else {
    return "PC";
  }
}

// jsbridge的方法不是很好用，摘取自判断ua的方式来判断app
export function isInApp() {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.indexOf("xiaopeng") !== -1;
}


// 浏览器的hash路由补充为完整的url（专门给par-marketing页面）
function completedUrlOfHashByParMarketing(hash: string, pathname?: string) {
  const { origin } = location;
  const finalPathname = pathname || "/apps/charge-web/par-marketing.html";
  return `${origin}${finalPathname}#${hash}`;
}

/**
 * 检查支付宝域名限制
 * 微信终端访问支付宝域名时返回 false 并提示
 * @param url 要检查的URL
 * @returns 是否允许跳转
 */
function checkAlipayDomainRestriction(url: string): boolean {
  const { isWX, isWXApp } = getBrowserEnv();
  const alipayDomains = ['https://u.alipay.cn', 'https://render.alipay.com'];
  
  if ((isWX || isWXApp) && alipayDomains.some(domain => url.includes(domain))) {
    Toast.show({ content: "当前平台暂不支持打开，敬请谅解~" });
    return false;
  }
  return true;
}

// 原生新开webview
export function webviewOpen(url, data?) {
  XPMotorApi.getSdk().callHandler("openUrl", {
    url,
    data,
  });
}

/**
 * H5需要在APP\小程序\普通浏览器中都可以打开，特殊考虑小程序页面跳转需要使用小程序的跳转方式，封装一个H5页面跳转方法
 * @param url 完整的前端http 的url
 * @param appId 业务方H5的用户中心appid
 * @param params
 * @param hash 前端hash路由
 * @description v2版本在小鹏app内容，新开webview的方式跳转页面
 */
export function goToPageV2({
  url,
  appId = "",
  params,
  hash,
  pathname,
  isReplace = false,
}: {
  url?: string;
  appId?: string;
  params?: object;
  hash?: string;
  pathname?: string;
  isReplace?: boolean;
}) {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  const targetUrl = url
    ? url
    : hash && completedUrlOfHashByParMarketing(hash, pathname);
  console.log("goToPageV2 targetUrl", targetUrl);
  if (!targetUrl) return;
  
  // 前置检查：微信环境访问支付宝域名限制
  if (!checkAlipayDomainRestriction(targetUrl)) {
    return;
  }
  
  const config = {
    url: `/pages/webpage/index?pageUrl=${encodeURIComponent(
      transformToQueryUrl(targetUrl, params),
    )}&appId=${appId}`,
  };
  if (isXPApp) {
    if (isReplace) {
      window.location.href = transformToQueryUrl(targetUrl, params);
    } else {
      webviewOpen(transformToQueryUrl(targetUrl, params));
    }
  } else if (isWXApp) {
    wxInit().then((wx) =>
      // 是否覆盖当前页面
      isReplace
        ? wx.miniProgram.redirectTo(config)
        : wx.miniProgram.navigateTo(config),
    );
  } else if (isMyApp) {
    myInit().then((my) =>
      isReplace ? my.redirectTo(config) : my.navigateTo(config),
    );
  } else {
    setTimeout(() => {
      const { origin, pathname, hash } = window.location;
      let hashPath = hash;
      if (hash) {
        hashPath = hash.split("?")?.[0] || hashPath;
      }
      const hashUrl = `${origin}${pathname}${hashPath}`;
      window.location.href = transformToQueryUrl(targetUrl, params);
      if (targetUrl.indexOf(hashUrl) == 0) {
        // 如果跳转地址 hash 路径一直，需要reload
        window.location?.reload?.();
      }
    }, 300);
  }
}

/**
 * 解析 jumpParam 参数
 * @param jumpParam JSON 字符串格式的跳转参数
 * @returns 解析后的对象，解析失败返回 null
 */
function parseJumpParam(jumpParam?: string): { _jumpPage?: string } | null {
  if (!jumpParam) return null;
  try {
    console.log('JSON.parse', JSON.parse(jumpParam))
    return JSON.parse(jumpParam);
  } catch {
    return null;
  }
}

/**
 * H5需要跳转APP/小程序原生页面
 * @param urlList [app链接，微信链接，支付宝链接]
 * @param jumpParam 跳转参数（JSON字符串），用于判断跳转类型
 */
export function NativeByUrlList(urlObj: { [key: string]: string }, jumpParam?: string) {
  const { isWXApp } = getBrowserEnv();
  console.log("NativeByUrlList", urlObj, jumpParam);

  // 前置检查：微信环境访问支付宝域名限制
  const hasAlipayDomain = Object.values(urlObj).some(url => 
    url && !checkAlipayDomainRestriction(url)
  );
  
  if (hasAlipayDomain) {
    return;
  }

  // 如果微信和支付宝链接相同，使用统一的小程序链接
  if (urlObj["2"] && urlObj["3"] && urlObj["2"] === urlObj["3"]) {
    goToNativePage({
      mini: urlObj["2"],
      app: urlObj["1"],
      browser: urlObj["1"],
      jumpParam,
    });
  } else {
    // 微信和支付宝链接不同，根据环境选择对应链接
    goToNativePage({
      mini: isWXApp ? urlObj["2"] : urlObj["3"],
      app: urlObj["1"],
      browser: urlObj["1"],
      jumpParam,
    });
  }
}

/**
 * 判断是否为 scheme 链接
 * @param url 待检测的链接
 * @returns 是否为 scheme 链接（如 xiaopeng://, weixin://, alipays:// 等）
 */
function isSchemeUrl(url: string): boolean {
  // 排除 http/https，匹配其他 scheme 如 xiaopeng://, weixin://, alipays:// 等
  return /^(?!https?:\/\/)[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(url);
}

/**
 * H5跳转APP/小程序原生页面
 * 
 * 跳转逻辑：
 * 1. scheme 链接（如 xiaopeng://xxx）+ 小鹏APP内：直接使用 window.location.href 跳转
 * 2. http/https 链接或浏览器环境：调用 goToPageV2 处理
 * 3. 小鹏APP内（非 scheme、非 http/https）：通过 jsbridge openUrl 打开
 * 4. 微信/支付宝小程序内：使用小程序 navigateTo/switchTab 跳转
 * 5. 普通浏览器：使用 window.location.href 跳转
 * 
 * @param urlConfig.mini 小程序环境跳转链接
 * @param urlConfig.app 小鹏APP环境跳转链接
 * @param urlConfig.browser 浏览器环境跳转链接（可选，默认使用 app 链接）
 * @param urlConfig.appParams APP环境额外参数（可选）
 * @param urlConfig.jumpParam 跳转参数（JSON字符串），用于判断跳转类型
 */
export function goToNativePage(urlConfig: {
  mini: string;
  app: string;
  browser?: string;
  appParams?: object;
  jumpParam?: string;
}) {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  const env = isXPApp ? "app" : isWXApp || isMyApp ? "mini" : "browser";
  const url = urlConfig[env];
  
  // 解析 jumpParam，检查是否为第三方小程序跳转且 url 为空
  const parsedJumpParam = parseJumpParam(urlConfig.jumpParam);
  console.log('parsedJumpParam', parsedJumpParam, urlConfig)
  if (parsedJumpParam?._jumpPage === "third-party-miniprogram" && !url) {
    Toast.show({ content: "当前平台暂不支持打开，敬请谅解~" });
    return;
  }
  
  if (!url) return;

  if (isSchemeUrl(url) && isXPApp) {
    window.location.href = url;
    return;
  }

  if (
    url.indexOf("http://") === 0 ||
    url.indexOf("https://") === 0 ||
    env === "browser"
  ) {
    goToPageV2({ url });
  } else if (isXPApp) {
    setTimeout(() => {
      XPMotorApi.getSdk().callHandler("openUrl", { url, data: urlConfig.appParams });
    }, 300);
  } else if (isWXApp) {
    console.log("小程序跳转isWXApp-------" + url);
    wxInit().then((wx) =>
      wx.miniProgram.navigateTo({
        url,
        complete: (event) => {
          console.log("navigateTo event", event);
          wx.miniProgram.switchTab({
            url,
            complete: (event) => {
              console.log("switchTab event", event);
            },
          });
        },
      }),
    );
  } else if (isMyApp) {
    console.log("小程序跳转isMyApp-------" + url);
    myInit().then((my) =>
      my.navigateTo({
        url,
        complete: (event) => {
          console.log("navigateTo event", event);
          my.switchTab({
            url,
            complete: (event) => {
              console.log("switchTab event", event);
            },
          });
        },
      }),
    );
  } else {
    window.location.href = url;
  }
}

// 关闭浏览器
export function closeWindow() {
  const { isWXApp, isMyApp } = getBrowserEnv();
  if (isWXApp) {
    console.log("isWXApp-closeWindow");
    wxInit().then((wx) => wx.miniProgram.navigateBack());
  } else if (isMyApp) {
    console.log("isMyApp-closeWindow");
    myInit().then((my) => my.miniProgram.navigateBack());
  } else {
    XPMotorApi.getSdk().callHandler("closeWindow");
  }
}

/**
 * H5需要在APP\小程序\普通浏览器中都可以打开，特殊考虑小程序页面，封装一个back方法
 */
export function goBackV2() {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  const router = window.history;

  if (isXPApp) {
    XPMotorApi.getSdk().callHandler("goBack");
  } else if (isWXApp) {
    wxInit().then((wx) => {
      wx.miniProgram.navigateBack();
      setTimeout(() => {
        wx.miniProgram.switchTab({
          url: `/pages/station-list/index`,
        });
      }, 300);
    });
  } else if (isMyApp) {
    myInit().then((my) => {
      my.navigateBack();
      setTimeout(() => {
        my.switchTab({
          url: `/pages/station-list/index`,
        });
      }, 300);
    });
  } else {
    router.back();
  }
}

/**
 * 兼容两端系统返回问题
 */
export function goBackCompatible() {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  const router = window.history;
  if (isXPApp) {
    XPMotorApi.getSdk().callHandler("goBack");
  } else if (isWXApp) {
    wxInit().then((wx) => {
      wx.miniProgram.navigateBack();
      setTimeout(() => {
        wx.miniProgram.switchTab({
          url: `/pages/station-list/index`,
        });
      }, 300);
    });
  } else if (isMyApp) {
    myInit().then((my) => {
      my.navigateBack();
      setTimeout(() => {
        my.switchTab({
          url: `/pages/station-list/index`,
        });
      }, 300);
    });
  } else {
    router.back();
  }
}

/**
 * 导航栏隐藏
 * 注意: 针对鸿蒙处理，一级页面设置了hide之后，二级页面要主动调用show，否则会导致二级页面导航栏未展示而样式错乱
 */
export function navHide(darkMode?: boolean) {
  if (isInApp()) {
    if (getPlatform() === "iOS") {
      bridge.callHandler("modifyNavBarAlpha", { alpha: 0 });
    } else {
      bridge.callHandler("setFullScreen", {
        fullScreen: true,
        darkMode: !!darkMode,
      });
    }
  }
}

/**
 * 导航栏恢复
 */
export function navShow() {
  if (isInApp()) {
    if (getPlatform() === "iOS") {
      bridge.callHandler("modifyNavBarAlpha", { alpha: 1 });
    } else {
      bridge.callHandler("setFullScreen", {
        fullScreen: false,
        darkMode: false,
      });
    }
  }
}

/**
 * 返回当前页面的环境，依赖于当前域名
 */
export function getClientEnv() {
  let env = "test";
  if (window.location.href.indexOf("static.deploy-test.xiaopeng.com") !== -1) {
    env = "deploy";
  } else if (window.location.href.indexOf("static.xiaopeng.com") !== -1) {
    env = "product";
  }
  return env;
}

export function downloadImage(src, imgName) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = src;
    image.setAttribute("crossOrigin", "anonymous");
    image.onerror = function () {
      reject("保存失败");
    };
    image.onload = function () {
      const c = document.createElement("canvas");
      c.width = image.width;
      c.height = image.height;
      c.getContext("2d")?.drawImage(image, 0, 0, image.width, image.height);
      const a = document.createElement("a");
      a.download = imgName;
      a.href = c.toDataURL("image/png");
      a.click();
      resolve("");
    };
  });
}

// 防抖函数
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): T {
  let timeout: NodeJS.Timeout | null = null;
  return function (_this: any, ...args: Parameters<T>) {
    const context = _this;
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  } as T;
}

export const goToStationList = () => {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  if (isXPApp) {
    goToNativePage({ app: "xiaopeng://charging/manageNew", mini: "" });
  } else if (isWXApp) {
    wxInit().then((wx) =>
      wx.miniProgram.switchTab({
        url: `/pages/station-list/index`,
      }),
    );
  } else if (isMyApp) {
    myInit().then((my) =>
      my.switchTab({
        url: `/pages/station-list/index`,
      }),
    );
  } else {
    Toast.show("请下载小鹏APP查看");
  }
};

// 调用原生定位方法
export function requestLocation(): Promise<any> {
  return XPMotorApi.getSdk().callHandler("requestLocation", {});
}

/**
 * Promise 超时包装器
 */
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("定位超时")), ms),
  );
  return Promise.race([promise, timeout]);
};

/**
 * 创建临时地图容器
 */
const createTempContainer = () => {
  let container = document.getElementById("amap-temp-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "amap-temp-container";
    container.style.cssText =
      "width:0;height:0;position:absolute;visibility:hidden;";
    document.body.appendChild(container);
  }
  return container.id;
};

/**
 * 获取高德定位promise
 */
export const getLocationGeoPro = (): Promise<{ lng: number; lat: number }> => {
  const locationPromise = new Promise<{ lng: number; lat: number }>(
    (resolve, reject) => {
      loadScript(
        "https://webapi.amap.com/maps?v=1.4.12&key=97005306e21be3a9a88ac7393a26f1ca",
        "par-web-script",
      )
        .then(() => {
          const AMap = (window as any).AMap;
          if (!AMap) {
            return reject(new Error("高德地图 SDK 加载失败"));
          }

          const containerId = createTempContainer();
          const mapObj = new AMap.Map(containerId);

          mapObj.plugin("AMap.Geolocation", function () {
            const geolocation = new AMap.Geolocation({
              enableHighAccuracy: true,
              timeout: 8000, // 内部定位超时 8 秒，留 2 秒给外层
              maximumAge: 1000,
              GeoLocationFirst: true,
            });
            mapObj.addControl(geolocation);
            geolocation.getCurrentPosition();

            AMap.event.addListener(geolocation, "complete", (result) => {
              mapObj.destroy();
              resolve({
                lng: result.position.lng,
                lat: result.position.lat,
              });
            });

            AMap.event.addListener(geolocation, "error", (result) => {
              mapObj.destroy();
              console.error(
                "高德定位失败:",
                result,
                result.location_type,
                result.info,
              );
              reject(
                new Error(`高德定位失败: ${result.message || result.info}`),
              );
            });
          });
        })
        .catch((err) => {
          reject(new Error(`高德地图脚本加载失败: ${err.message}`));
        });
    },
  );

  return withTimeout(locationPromise, 10000); // 整体 10 秒超时
};

/**
 * 获取高德经纬度翻译地址
 */
export const getLocationToAddress = ({ lng, lat }) => {
  return new Promise((resolve) => {
    try {
      loadScript(
        "https://webapi.amap.com/maps?v=1.4.12&key=97005306e21be3a9a88ac7393a26f1ca",
        "par-web-script",
      ).then(() => {
        const AMap = (window as any).AMap;
        const map = new AMap.Map("", {});
        map.plugin("AMap.Geocoder", () => {
          const geocoder = new AMap.Geocoder({});
          geocoder.getAddress([lng, lat], function (status, result) {
            if (status === "complete" && result.info === "OK") {
              console.log("getLocationToAddress", result);
              resolve(result?.regeocode?.formattedAddress);
            } else {
              resolve("");
            }
          });
        });
      });
    } catch (error) {
      console.error("getLocationToAddress error", error);
    }
  });
};

/**
 * 统一定位获取方法
 * 优先级：APP定位 -> 高德定位 -> 默认位置（广州市）
 * @returns Promise<{ lng: number, lat: number, address?: string, isUseDefault?: boolean }>
 */
export const getUnifiedLocation = async (): Promise<{
  lng: number;
  lat: number;
  address?: string;
  isUseDefault?: boolean;
}> => {
  const { isXPApp } = getBrowserEnv();
  // 默认定位点（广州市）
  const defaultPoint = {
    lng: 113.38477,
    lat: 23.15944,
    citycode: "020",
    cityName: "广州市",
  };
  const lng = getParamsFromHash("lng") || "";
  const lat = getParamsFromHash("lat") || "";

  // 如果在APP内，优先使用APP定位
  if (isXPApp) {
    try {
      let locationData;
      if (lng && lat) {
        locationData = {
          lng: Number(lng),
          lat: Number(lat),
        };
      } else {
        const res = await requestLocation();
        if (res.code === 0) {
          locationData = {
            lng: res.data.coords.longitude,
            lat: res.data.coords.latitude,
          };
        } else {
          throw new Error("APP定位失败");
        }
      }
      try {
        const address = await getLocationToAddress(locationData);
        return {
          ...locationData,
          address,
          isUseDefault: false,
        };
      } catch (error) {
        console.error("地址解析失败:", error);
        return {
          ...locationData,
          address: `${locationData.lng.toFixed(3)},${locationData.lat.toFixed(3)}`,
          isUseDefault: false,
        };
      }
    } catch (err) {
      console.error("APP定位失败:", err);
      // APP定位失败，继续尝试Web定位
    }
  }

  // Web端定位（高德定位）
  try {
    let locationData;
    if (lng && lat && Number(lng) && Number(lat)) {
      locationData = {
        lng: Number(lng),
        lat: Number(lat),
      };
    } else {
      locationData = (await getLocationGeoPro()) as {
        lng: number;
        lat: number;
      };
    }
    console.log("getLocationGeoPro", locationData);
    // 获取地址信息
    try {
      const address = await getLocationToAddress(locationData);
      return {
        ...locationData,
        address,
        isUseDefault: false,
      };
    } catch (error) {
      console.error("地址解析失败:", error);
      return {
        ...locationData,
        address: `${locationData.lng.toFixed(3)},${locationData.lat.toFixed(3)}`,
        isUseDefault: false,
      };
    }
  } catch (error) {
    console.error("Web定位失败:", error);

    // 定位失败时使用默认位置（广州市）
    const defaultLocation = { lng: defaultPoint.lng, lat: defaultPoint.lat };

    try {
      const address = await getLocationToAddress(defaultLocation);
      return {
        ...defaultLocation,
        address,
        isUseDefault: true,
      };
    } catch (error) {
      console.error("默认位置地址解析失败:", error);
      return {
        ...defaultLocation,
        address: defaultPoint.cityName,
        isUseDefault: true,
      };
    }
  }
};

/**
 * 上拉加载更多
 */
export const onScrollLoadMore = (props: {
  scrollDomRef: React.RefObject<HTMLDivElement>;
  list: any[];
  pageNo: number;
  pageSize: number;
  setPageNo: (pageNo: number) => void;
  callback: () => void;
}) => {
  const {
    scrollDomRef,
    list,
    pageNo,
    pageSize,
    setPageNo = () => { },
    callback = () => { },
  } = props;

  const dom: any = scrollDomRef.current;
  if (dom) {
    const isEnd = dom.scrollTop >= dom.scrollHeight - dom.clientHeight;
    if (isEnd) {
      // 列表数据加载结束
      if (list.length < pageNo * pageSize) {
        return;
      }
      setPageNo(pageNo + 1);
      callback();
    }
  }
};

export const priceFormat = (price) => {
  const stringPrice = Number(price / 100).toFixed(2);
  const priceArr = stringPrice.split(".");
  if (priceArr[1] === "00" || priceArr[1] === "0") {
    return priceArr[0];
  } else if (priceArr[1]?.[1] === "0") {
    return priceArr[0] + "." + priceArr[1][0];
  } else {
    return stringPrice;
  }
};

/**
 * 复制文本到剪切板
 */
export const copyToClipBoard = (
  str: string,
  showToast: boolean = false,
  toastText: string = "已复制",
) => {
  const input = document.createElement("input");
  input.value = str;
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);

  showToast &&
    Toast.show({
      content: toastText,
      duration: 800,
    });
};

/**
 * 判断一个元素是否在可视窗口内
 * @param element
 * @returns
 */
export function isInViewPort(element, navBarHeight = 0) {
  const viewWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const { top, right, bottom, left } = element.getBoundingClientRect();

  if (navBarHeight > 0) {
    return (
      top >= navBarHeight &&
      left >= 0 &&
      right <= viewWidth &&
      bottom <= viewHeight
    );
  }

  return top >= 0 && left >= 0 && right <= viewWidth && bottom <= viewHeight;
}

// 回调数据给上一层页面
export function callbackData(data) {
  XPMotorApi.getSdk().callHandler("setResult", data);
}

/**
 * 监听webview关闭事件
 */
export function onWebviewClose(callback) {
  bridge.registerHandler("onClose", function (data, responseCallback) {
    if (typeof callback === "function") {
      callback();
    }
    responseCallback({}); // 回调给原生
  });
}

/*
 *  预加载图片
 */
export const preloadImages = (config) => {
  config.forEach((item) => {
    const img = new Image();
    img.src = item;
  });
};

// 控制数字小数点后位数， 末尾的0不显示
export const formatNumber = (num: number, precision: number) => {
  if (num === 0) {
    return "0";
  }
  if (!num) {
    return "";
  }
  return num.toFixed(precision).replace(/(\.0+|0+)$/, "");
};

export const showLoading = (text?: string) => {
  Loading.show(text || "加载中...");
};

export const hideLoading = () => {
  Loading.hide();
};
